<script setup lang="ts">
import { ChevronDown, CirclePlus } from 'lucide-vue-next'
import { computed, onMounted, ref, watch } from 'vue'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/foundation/accordion'
import { Button } from '@/foundation/button'
import {
  Combobox,
  ComboboxAnchor,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '@/foundation/combobox'
import { getConsistentColor, getContactInitials } from '@/utils/utils'

// Define a proper interface for options
type OptionType = {
  id: string
  name?: string
  label?: string
  bg?: string
  text?: string
  logoUrl?: string
  avatar?: string
  icon?: any
  children?: OptionType[]
  filteredChildren?: OptionType[]
  parent?: {
    id: string
    name: string
  }
}

const props = defineProps({
  options: {
    type: Array as () => OptionType[],
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  value: {
    type: Object as () => OptionType,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  emptyMessage: {
    type: String,
    default: 'No results found.',
  },
  placeholder: {
    type: String,
    default: '',
  },
  insideText: {
    type: String,
    default: '',
  },
  defaultOption: {
    type: String,
    default: null,
  },
  padding: {
    type: String,
    default: '',
  },
  height: {
    type: Number,
    default: 40,
  },
  showLogo: {
    type: Boolean,
    default: false,
  },
  showIcon: {
    type: Boolean,
    default: false,
  },
  showSpace: {
    type: Boolean,
    default: false,
  },
  showShadow: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['update:value', 'openNewContactDialog'])

const open = ref(false)
const key = ref(0)

const handleSelect = (ev: any) => {
  emit('update:value', ev.detail.value)
  open.value = false
  searchInput.value = ''
}

const displayValue = (val: any): string => {
  if (!val) return ''

  if (val.parent) {
    return `${val.parent.name} > ${val?.label || val?.name || ''}`
  }

  const defaultOption = (props.options as OptionType[])?.find((o: OptionType) => o?.id === val?.id || o?.id === val)

  if (defaultOption) {
    const result = props.label || defaultOption.label || defaultOption.name || ''

    return result
  }

  const result = props.label || (val?.label ? val?.label : val?.name) || ''

  return result
}

onMounted(() => {
  if (props.defaultOption) {
    const defaultOption = (props.options as OptionType[]).find((option: OptionType) => option.label === props.defaultOption)

    if (defaultOption) {
      emit('update:value', defaultOption)
    }
  }
})

watch(
  () => props.value,
  () => {
    if (!props.value?.id) {
      key.value++
    }
  },
  { immediate: true, deep: true },
)

const hasChildren = (option: OptionType) => {
  return option.children && option.children.length > 0
}

const searchInput = ref('')

const filteredOptions = computed(() => {
  if (!searchInput.value) return props.options

  const lowerValue = searchInput.value.toLowerCase()

  return props.options.filter((option: OptionType) => {
    // Check if the parent matches
    const parentName = option.label || option.name || ''
    const parentMatches = parentName.toLowerCase().includes(lowerValue)

    // If option has children, check if any children match
    if (option.children && option.children.length > 0) {
      const childrenMatch = option.children.some((child: OptionType) => {
        const childName = child.label || child.name || ''
        const matches = childName.toLowerCase().includes(lowerValue)

        return matches
      })

      // If parent matches, include all children
      if (parentMatches) {
        option.filteredChildren = option.children

        return true
      }

      // If any children match but parent doesn't, create a filtered version
      if (childrenMatch) {
        option.filteredChildren = option.children.filter((child: OptionType) =>
          (child.label || child.name || '').toLowerCase().includes(lowerValue),
        )

        return true
      }

      return false
    }

    // For options without children, just check the option itself
    return parentMatches
  })
})

// Update the input handler to capture search text
const handleInputChange = (event: any) => {
  searchInput.value = event.target.value
}

const handleBlur = () => {
  if (!props.value?.id) {
    searchInput.value = ''
  }
}

const addNewContact = () => {
  emit('openNewContactDialog')
}
</script>

<template>
  <Combobox :key="key" by="label" :disabled="disabled" :default-value="props.value">
    <ComboboxAnchor class="w-full" :style="{ '--combobox-anchor-width': '100%' }">
      <ComboboxTrigger as-child :disabled="disabled">
        <div class="relative w-full items-center">
          <div class="left-3.5 top-2 z-10 absolute">
            <div v-if="showIcon && props.value?.icon" class="w-6 h-6 rounded p-0.5 flex items-center justify-center">
              <component :is="props.value.icon" class="w-4 h-4" />
            </div>
            <img
              v-else-if="(props.value.logoUrl?.logoUrl || props.value.avatar) && showLogo && props.value?.name"
              class="w-6 h-6 cursor-pointer object-contain"
              :src="props.value.logoUrl ?? props.value.avatar"
              alt="logo"
            />
            <div
              v-else-if="showLogo && props.value?.name"
              :class="[
                'w-6 h-6 flex text-[10px] items-center justify-center text-white font-semibold rounded-full cursor-pointer',
                getConsistentColor(props.value?.id || ''),
              ]"
            >
              {{ getContactInitials(props.value?.label || props.value?.name || '') }}
            </div>
          </div>

          <ComboboxInput
            class="autocomplete-ellipsis pr-7"
            :class="{
              'pl-11': (showSpace && label?.length) || ((showLogo || showIcon) && props.value?.name),
              'shadow-none': !showShadow,
            }"
            :style="{
              height: height + 'px',
              backgroundColor: value.bg ?? '',
              color: value.text,
              fontWeight: value.text ? '600' : '400',
            }"
            :display-value="displayValue"
            :placeholder="placeholder"
            @input="handleInputChange"
            @blur="handleBlur"
          />
          <span
            :class="['absolute end-0 inset-y-0 flex items-center justify-center px-3', { 'pointer-events-none': disabled }]"
          >
            <ChevronDown class="size-4 text-muted-foreground cursor-pointer" />
          </span>
        </div>
      </ComboboxTrigger>
    </ComboboxAnchor>

    <ComboboxList class="w-full combobox-list-fix !max-h-56 !overflow-y-auto">
      <Button
        v-if="props.placeholder === 'Select Contact' && $attrs?.selectedCompany?.id"
        type="button"
        variant="ghost"
        class="w-full h-auto py-3 px-3 text-left flex items-center !rounded-none border-b border-border"
        @click.prevent.stop="addNewContact"
      >
        <CirclePlus class="w-4 h-4 shrink-0" />
        <span class="flex-1 block truncate">
          Create New Contact for
          <span>
            {{ $attrs?.selectedCompany?.name }}
          </span>
        </span>
      </Button>

      <ComboboxEmpty class="w-full">
        {{ emptyMessage }}
      </ComboboxEmpty>

      <ComboboxGroup v-if="options?.length" class="w-full" :class="[padding]">
        <span v-if="insideText" class="block text-xs font-semibold text-muted-foreground ml-2 mb-1 mt-1">
          {{ insideText }}
        </span>

        <template v-for="option in filteredOptions" :key="option.id">
          <div v-if="hasChildren(option)" class="w-full">
            <Accordion type="single" collapsible class="w-full border-0 shadow-none">
              <AccordionItem value="item-1" class="border-0">
                <AccordionTrigger class="py-1 px-2 hover:no-underline font-normal hover:bg-sidebar-accent rounded">
                  <div class="flex items-center">
                    {{ option?.label ?? option?.name }}
                  </div>
                </AccordionTrigger>

                <AccordionContent>
                  <ComboboxItem
                    :value="option"
                    :style="{ backgroundColor: option.bg, color: option.text, fontWeight: option.text ? '600' : '400' }"
                    class="w-full cursor-pointer flex justify-start items-center pl-4"
                    @select="handleSelect"
                  >
                    <span class="mr-2">—</span>
                    {{ option?.label ?? option?.name }} (Select this category)
                  </ComboboxItem>

                  <ComboboxItem
                    v-for="child in option.filteredChildren || option.children || []"
                    :key="child.id"
                    :value="{ ...child, parent: { id: option.id, name: option.name } }"
                    :style="{ backgroundColor: child.bg, color: child.text, fontWeight: child.text ? '600' : '400' }"
                    class="w-full cursor-pointer flex justify-start pl-4"
                    @select="handleSelect"
                  >
                    <span class="mr-2">—</span>
                    {{ child?.label ?? child?.name }}
                  </ComboboxItem>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          <ComboboxItem
            v-else
            :value="option"
            :style="{ color: option.text, fontWeight: option.text ? '600' : '400' }"
            class="w-full cursor-pointer flex justify-start items-center"
            @select="handleSelect"
          >
            <div
              v-if="showIcon && option?.icon"
              class="w-6 h-6 rounded p-0.5 mr-2 flex items-center justify-center"
              :style="{ backgroundColor: option.bg || 'transparent' }"
            >
              <component :is="option.icon" class="w-4 h-4" />
            </div>
            <img
              v-else-if="(option?.logoUrl || option?.avatar) && showLogo"
              class="w-6 h-6 cursor-pointer object-contain"
              :src="option.logoUrl ?? option.logoUrl"
              alt="logo"
            />
            <div
              v-else-if="showLogo"
              :class="[
                'w-6 h-6 flex text-[10px] items-center justify-center text-white font-semibold rounded-full cursor-pointer',
                getConsistentColor(option?.id || ''),
              ]"
            >
              {{ getContactInitials(option?.label || option?.name || '') }}
            </div>

            {{ option?.label ?? option?.name }}
          </ComboboxItem>
        </template>
      </ComboboxGroup>
    </ComboboxList>
  </Combobox>
</template>

<style>
[data-reka-popper-content-wrapper] {
  pointer-events: auto !important;
  z-index: 9999 !important;
  min-width: var(--reka-popper-anchor-width) !important;
  width: var(--reka-popper-anchor-width) !important;
}

[data-reka-popper-content-wrapper] > div {
  width: 100% !important;
}

.autocomplete-ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  display: block;
}
</style>
